import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Divider, Menu, Space, Layout, Modal, Input } from 'antd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { addMenu, clearMenu } from 'redux/slices/menu';
import { useTranslation } from 'react-i18next';
import LangModal from './lang-modal';
import getSystemIcons from 'helpers/getSystemIcons';
import NotificationBar from './notificationBar';
import { navCollapseTrigger } from 'redux/slices/theme';
import ThemeConfigurator from './theme-configurator';
import i18n from 'configs/i18next';
import { RiArrowDownSFill } from 'react-icons/ri';
import NavProfile from './nav-profile';
import { batch } from 'react-redux';
import { clearUser } from 'redux/slices/auth';
import { data as allRoutes } from 'configs/menu-config';
import { sanitizeUserUrls } from 'helpers/role-access';

import { clearBags } from 'redux/slices/cart';
const { Sider } = Layout;
const { SubMenu } = Menu;

// Extracted filterUserRoutes outside to be a pure function
const filterUserRoutesPure = (routes, myShopType, paymentType, systemRefund) => {
  let list = routes;
  if (myShopType === 'shop') {
    list = list?.filter((item) => item?.name !== 'brands');
  }
  if (paymentType === 'admin') {
    list = list?.filter((item) => item?.name !== 'payments');
  }
  if (systemRefund === '0') {
    list = list?.filter((item) => item?.name !== 'refunds');
  }
  return list;
};

const Sidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useSelector((state) => state.auth, shallowEqual);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { system_refund, payment_type, by_subscription } = useSelector(
    (state) => state.globalSettings.settings,
    shallowEqual,
  );

  // Construct full name from firstname and lastname
  const fullName = user?.fullName;
  const { navCollapsed } = useSelector(
    (state) => state.theme.theme,
    shallowEqual,
  );
  const { languages } = useSelector((state) => state.formLang, shallowEqual);
  const dispatch = useDispatch();
  const [langModal, setLangModal] = useState(false);
  const { myShop } = useSelector((state) => state.myShop, shallowEqual);
  const { theme } = useSelector((state) => state.theme, shallowEqual);
  const parcelMode = useMemo(
    () => !!theme.parcelMode && user?.role === 'admin',
    [theme, user],
  );

  const filterUserRoutes = useCallback( // Wrap in useCallback
    (routesToFilter) =>
      filterUserRoutesPure(routesToFilter, myShop.type, payment_type, system_refund),
    [myShop.type, payment_type, system_refund] // Dependencies for useCallback
  );

  const visibleUserUrls = useMemo(
    () => sanitizeUserUrls(user?.role, user?.urls || []),
    [user],
  );

  const routes = useMemo(() => {
    const isSubscriptionEnabled = by_subscription === '1';
    const excludeRoutes = { routes: [], subRoutes: [] };
    if (!isSubscriptionEnabled) {
      excludeRoutes.subRoutes.push(
        'subscriptions',
        'my.subscriptions',
        'shop.subscriptions',
      );
    }
    return filterUserRoutes(
      visibleUserUrls
        ?.filter((item) => !excludeRoutes.routes.includes(item.name))
        .map((item) => ({
          ...item,
          submenu: item.submenu?.filter(
            (sub) => !excludeRoutes.subRoutes.includes(sub.name),
          ),
        })),
    );
  }, [visibleUserUrls, by_subscription, filterUserRoutes]); // Added by_subscription and filterUserRoutes

  // Recursive function to find active menu item
  const findActiveItem = useCallback((routesList, path) => {
    for (const item of routesList || []) {
      if (item.url && path === `/${item.url}`) {
        return item;
      }
      if (item.url && path.includes(item.url) && item.url !== '') {
        // Find the longest match to avoid accidental matches
        return item;
      }
      if (item.submenu) {
        const found = findActiveItem(item.submenu, path);
        if (found) return found;
      }
      if (item.children) {
        const found = findActiveItem(item.children, path);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const active = useMemo(() => findActiveItem(routes, pathname), [routes, pathname, findActiveItem]);

  const [searchTerm, setSearchTerm] = useState('');
  const data = useMemo(
    () => (parcelMode ? allRoutes.parcel : routes),
    [parcelMode, routes],
  );
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);
  const autoCollapsed = useRef(false);
  const prevIsMobile = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const wasMobile = prevIsMobile.current;

    if (wasMobile === null) {
      prevIsMobile.current = isMobile;
      if (isMobile && !navCollapsed) {
        dispatch(navCollapseTrigger());
        autoCollapsed.current = true;
      }
      return;
    }

    if (isMobile && !wasMobile && !navCollapsed) {
      dispatch(navCollapseTrigger());
      autoCollapsed.current = true;
    } else if (!isMobile && wasMobile && navCollapsed && autoCollapsed.current) {
      dispatch(navCollapseTrigger());
      autoCollapsed.current = false;
    }

    prevIsMobile.current = isMobile;
  }, [dispatch, isMobile, navCollapsed]);

  const [openKeys, setOpenKeys] = useState([]);
  // Sync openKeys with current pathname
  useEffect(() => {
    if (!pathname || !data || navCollapsed) return;
    const findRootKey = (routesList) => {
      for (let i = 0; i < routesList.length; i++) {
        const item = routesList[i];
        if (item.submenu) {
          const foundChild = item.submenu.some(sub =>
            (sub.url && pathname.includes(sub.url) && sub.url !== '') ||
            sub.children?.some(child => child.url && pathname.includes(child.url))
          );
          if (foundChild) return item.id + '_' + i;
        }
      }
      return null;
    };
    const rootKey = findRootKey(data);
    if (rootKey) {
      setOpenKeys((prev) => {
        if (prev.includes(rootKey)) return prev;
        return [...prev, rootKey];
      });
    }
  }, [pathname, data, navCollapsed]);

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const addNewItem = (item) => {
    if (typeof item.url === 'undefined') return;
    if (item.name === 'logout') {
      setIsModalVisible(true);
      return;
    }
    const data = {
      ...item,
      icon: undefined,
      children: undefined,
      refetch: true,
    };
    dispatch(addMenu(data));
    navigate(`/${item.url}`);
  };

  const menuTrigger = (event) => {
    event.stopPropagation();
    autoCollapsed.current = false;
    dispatch(navCollapseTrigger());
  };

  const addMenuItem = (payload) => {
    const data = { ...payload, icon: undefined };
    dispatch(addMenu(data));
  };

  const handleOk = () => {
    batch(() => {
      dispatch(clearUser());
      dispatch(clearMenu());
      dispatch(clearBags());
    });
    setIsModalVisible(false);
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleCancel = () => setIsModalVisible(false);

  function getOptionList(routes) {
    const optionTree = [];
    routes?.forEach((item) => { // Changed .map to .forEach
      optionTree.push(item);
      item?.submenu?.forEach((sub) => { // Changed .map to .forEach
        optionTree.push(sub);
        sub?.children?.forEach((child) => { // Changed .map to .forEach
          optionTree.push(child);
        });
      });
    });
    return optionTree;
  }

  const optionList = useMemo(() => getOptionList(data), [data]);

  const menuList = useMemo(() => {
    return searchTerm.length > 0
      ? optionList.filter((input) =>
        t(input?.name ?? '')
          .toUpperCase()
          .includes(searchTerm.toUpperCase()),
      )
      : data;
  }, [searchTerm, optionList, data, t]); // Added 't' to dependencies

  const selectedKeys = useMemo(
    () => (active ? [String(active.id)] : []),
    [active],
  );

  const renderExpandIcon = useCallback(
    ({ isOpen }) => (
      <RiArrowDownSFill
        size={18}
        className={`menu-expand-icon ${isOpen ? 'open' : ''}`}
      />
    ),
    [],
  );

  return (
    <>
      <Sider
        theme={theme.currentTheme}
        className='navbar-nav side-nav'
        width={250}
        collapsed={navCollapsed}
        collapsedWidth={isMobile ? 0 : 80}
        style={{
          height: '100vh',
          position: 'fixed',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          left: 0,
          overflow: 'hidden',
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <NavProfile user={user} />
        </div>
        <div
          className='menu-collapse'
          onClick={menuTrigger}
          style={{ flexShrink: 0 }}
        >
          <MenuFoldOutlined />
        </div>
        {navCollapsed && (
          <div
            className='flex justify-content-center'
            style={{ flexShrink: 0 }}
          >
            <ThemeConfigurator />
          </div>
        )}

        {!navCollapsed ? (
          <Space
            className='mx-4 mt-2 d-flex justify-content-between'
            style={{ flexShrink: 0 }}
          >
            <span className='icon-button' onClick={() => setLangModal(true)}>
              <img
                className='globalOutlined'
                src={
                  (languages || []).find((item) => item.locale === i18n.language)
                    ?.img
                }
                alt={fullName}
              />
              <span className='default-lang'>{i18n.language}</span>
              <RiArrowDownSFill size={15} />
            </span>
            <span className='d-flex'>
              <ThemeConfigurator />
              <NotificationBar />
            </span>
          </Space>
        ) : (
          <div
            className='menu-unfold'
            onClick={menuTrigger}
            style={{ flexShrink: 0 }}
          >
            <MenuUnfoldOutlined />
          </div>
        )}
        <Divider style={{ margin: '10px 0', flexShrink: 0 }} />

        {!navCollapsed && (
          <span
            className='mt-2 mb-2 d-flex justify-content-center'
            style={{ flexShrink: 0 }}
          >
            <Input
              placeholder='search'
              style={{ width: '90%' }}
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
              prefix={<SearchOutlined />}
            />
          </span>
        )}

        <div
          className='sidebar-scroll-view'
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            width: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <div style={{ padding: '0 12px 16px' }}>
            <Menu
              theme={theme.currentTheme}
              mode='inline'
              selectedKeys={selectedKeys}
              openKeys={openKeys}
              onOpenChange={onOpenChange}
              className='modern-side-menu'
              inlineIndent={12}
              expandIcon={renderExpandIcon}
              style={{ border: 'none' }}
            >
              {menuList?.map((item, idx) =>
                item.submenu?.length > 0 ? (
                  <SubMenu
                    key={item.id + '_' + idx}
                    title={t(item.name)}
                    icon={getSystemIcons(item.icon)}
                  >
                    {item.submenu.map((submenu, idy) =>
                      submenu.children?.length > 0 ? (
                        <SubMenu
                          key={submenu.id + '_' + idy}
                          title={t(submenu.name)}
                          icon={getSystemIcons(submenu.icon)}
                          onTitleClick={() => addNewItem(submenu)}
                        >
                          {submenu.children?.map((sub, idk) => (
                            <Menu.Item
                              key={'child' + idk + sub.id}
                              icon={getSystemIcons(sub.icon)}
                            >
                              <Link
                                to={'/' + sub.url}
                                onClick={() => addMenuItem(sub)}
                              >
                                <span>{t(sub.name)}</span>
                              </Link>
                            </Menu.Item>
                          ))}
                        </SubMenu>
                      ) : (
                        <Menu.Item
                          key={submenu.id}
                          icon={getSystemIcons(submenu.icon)}
                        >
                          <Link
                            to={'/' + submenu.url}
                            onClick={() => addNewItem(submenu)}
                          >
                            <span>{t(submenu.name)}</span>
                          </Link>
                        </Menu.Item>
                      ),
                    )}
                  </SubMenu>
                ) : (
                  <Menu.Item key={item.id} icon={getSystemIcons(item.icon)}>
                    <Link to={'/' + item.url} onClick={() => addNewItem(item)}>
                      <span>{t(item.name)}</span>
                    </Link>
                  </Menu.Item>
                ),
              )}
            </Menu>
          </div>
        </div>
      </Sider>

      {isMobile && navCollapsed && (
        <div className='mobile-sider-trigger' onClick={menuTrigger}>
          <MenuUnfoldOutlined />
        </div>
      )}

      {langModal && (
        <LangModal
          visible={langModal}
          handleCancel={() => setLangModal(false)}
        />
      )}

      <Modal
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
      >
        <LogoutOutlined
          style={{ fontSize: '25px', color: '#08c' }}
          theme='primary'
        />
        <span className='ml-2'>{t('leave.site')}</span>
      </Modal>
    </>
  );
};
export default Sidebar;
